// `useApprovalState` — wraps GET /api/intermediaries/me/approval-state.
//
// Why we gate `enabled` on auth status:
//   AuthGate already only renders children when authenticated, so in
//   practice the query never fires before the token is ready. We still
//   pass `enabled` explicitly so this hook is correct in isolation —
//   if some future caller drops the gate the query won't run a
//   pre-auth request that would predictably 401.
//
// 401 handling: the apiClient surfaces it as ApiError(status=401). We do
// NOT special-case it here — Firebase's onAuthStateChanged is the
// authority on session expiry, and AuthGate flips to `unauthenticated`
// the moment the token is gone. Treating 401 as "blocked" inside this
// hook would race with the AuthGate transition and cause a flash.
//
// staleTime / refetch: matches the project default (30s, no
// refetchOnWindowFocus). We do NOT lower the stale window — approval
// state changes on the order of hours/days, not seconds, and a fresh
// "Aggiorna stato" CTA on the page covers the manual case.

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useAuth } from '@/auth/AuthGate';
import { apiClient } from '@/lib/apiClient';
import { qk } from '@/lib/queryKeys';
import type { ApprovalStateResponse } from '../types';

export function useApprovalState(): UseQueryResult<ApprovalStateResponse> {
  const { status } = useAuth();
  return useQuery<ApprovalStateResponse>({
    queryKey: qk.approvalState.me(),
    queryFn: () => apiClient.get<ApprovalStateResponse>('/api/intermediaries/me/approval-state'),
    enabled: status === 'authenticated',
    staleTime: 30_000,
  });
}
